/** Keep in sync with Layout.js persistent Drawer width. */
export const DASHBOARD_SIDEBAR_WIDTH_PX = 200;
export const DASHBOARD_SIDEBAR_MIN_WIDTH_PX = 180;
export const DASHBOARD_SIDEBAR_MAX_WIDTH_PX = 320;

/** Mobile drawer sheet radius. Desktop sidebar is a flat rail. */
export const DASHBOARD_SIDEBAR_SHELL_RADIUS = '8px';

/** Desktop white content panel corner radius. */
export const DASHBOARD_CONTENT_SHELL_RADIUS = '16px';

/** Gap between the open desktop sidebar and the white content panel. */
export const DASHBOARD_CONTENT_SIDEBAR_GAP_PX = 8;

/** Inset of the white content panel from the top, right, and bottom edges. */
export const DASHBOARD_CONTENT_EDGE_INSET_PX = 12;

/** Centered column for brand and influencer dashboard page content. */
export const DASHBOARD_PAGE_MAX_WIDTH_PX = 1040;

/**
 * Full-viewport drawer height. iOS Safari's 100vh includes the area behind the
 * bottom toolbar, so 100dvh (with 100vh fallback) keeps footer items visible.
 */
export const DASHBOARD_SIDEBAR_VIEWPORT_HEIGHT_SX = {
  height: '100vh',
  maxHeight: '100vh',
  '@supports (height: 100dvh)': {
    height: '100dvh',
    maxHeight: '100dvh',
  },
};

/** Desktop breakpoint when BrandSidebar sits beside main content. */
export const DASHBOARD_SIDEBAR_DESKTOP_MQL = '(min-width:1040px)';

/**
 * Left-edge hit width when the desktop drawer is collapsed.
 * Peek only while the pointer is within this many CSS pixels of the window's left edge
 * (or over the peeked drawer / its menus).
 */
export const DASHBOARD_SIDEBAR_PEEK_EDGE_PX = 4;

/** Live offset for fixed chrome (e.g. create-campaign footer). Set by Layout. */
export const DASHBOARD_SIDEBAR_WIDTH_CSS_VAR = '--dashboard-sidebar-width';

export const DASHBOARD_SIDEBAR_COLLAPSED_STORAGE_KEY = 'millet.dashboardSidebar.collapsed';
export const DASHBOARD_SIDEBAR_WIDTH_STORAGE_KEY = 'millet.dashboardSidebar.widthPx';

/** Mobile hamburger / peek control → Layout opens the dashboard drawer. */
export const MILLET_OPEN_DASHBOARD_SIDEBAR_EVENT = 'millet-tour-open-sidebar';

/** Navbar expand control → Layout expands the desktop drawer. */
export const MILLET_DESKTOP_SIDEBAR_EXPAND_EVENT = 'millet-desktop-sidebar-expand';

/** Layout publishes collapsed/width so Navbar toggle can show/hide. */
export const MILLET_DESKTOP_SIDEBAR_STATE_EVENT = 'millet-desktop-sidebar-state';

export function clampDashboardSidebarWidth(widthPx) {
  const n = Number(widthPx);
  if (!Number.isFinite(n)) return DASHBOARD_SIDEBAR_WIDTH_PX;
  return Math.min(
    DASHBOARD_SIDEBAR_MAX_WIDTH_PX,
    Math.max(DASHBOARD_SIDEBAR_MIN_WIDTH_PX, Math.round(n)),
  );
}

export function readDashboardSidebarCollapsed() {
  try {
    return localStorage.getItem(DASHBOARD_SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDashboardSidebarCollapsed(collapsed) {
  try {
    localStorage.setItem(DASHBOARD_SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // ignore quota / private mode
  }
}

export function readDashboardSidebarWidthPx() {
  try {
    const raw = localStorage.getItem(DASHBOARD_SIDEBAR_WIDTH_STORAGE_KEY);
    if (raw == null) return DASHBOARD_SIDEBAR_WIDTH_PX;
    return clampDashboardSidebarWidth(raw);
  } catch {
    return DASHBOARD_SIDEBAR_WIDTH_PX;
  }
}

export function writeDashboardSidebarWidthPx(widthPx) {
  try {
    localStorage.setItem(
      DASHBOARD_SIDEBAR_WIDTH_STORAGE_KEY,
      String(clampDashboardSidebarWidth(widthPx)),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function publishDesktopSidebarState({ collapsed, widthPx }) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MILLET_DESKTOP_SIDEBAR_STATE_EVENT, {
    detail: {
      collapsed: Boolean(collapsed),
      widthPx: clampDashboardSidebarWidth(widthPx ?? DASHBOARD_SIDEBAR_WIDTH_PX),
    },
  }));
}

export function expandDesktopDashboardSidebar() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(MILLET_DESKTOP_SIDEBAR_EXPAND_EVENT));
}

export function isEditableKeyboardTarget(target) {
  if (!target || !(target instanceof Element)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}
